import { type DocumentReference } from 'firebase/firestore';

import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { PanelViews } from 'types/PanelViews';
import { PropertyManagerType } from 'types/PropertyManagerType';

import AdsManager from './AdsManager';
import Assets from './Assets';
import BundlesManager from './BundlesManager';
import ClipboardManager from './ClipboardManager';
import { CreativePublicSchema, CreativeSchema } from './CreativeTypes';
import DocumentFolder, {
    applyDynamicFolderDefaults,
    type DocumentFolderParent,
} from './DocumentFolder';
import { type FolderSchema } from './DocumentFolderTypes';
import { type IDynamicProperty } from './DynamicProperties/IDynamicProperty';
import DynamicPropertiesManager from './DynamicProperties/Manager';
import { merge } from './DynamicProperties/merge';
import Tearsheets from './Tearsheets';
import type Template from './Template';
import Templates from './Templates';
import VideoBundlesManager from './VideoBundlesManager';

const DEFAULT_CONST_PROPERTIES: ReadonlyArray<
    [string, DynamicPropertyType, any]
> = [
    ['dimensions', DynamicPropertyType.DIMENSIONS, undefined],
    ['label', DynamicPropertyType.LABEL, undefined],
    ['backupImageFilesize', DynamicPropertyType.NUMBER, 40000],
    ['backupImageScale', DynamicPropertyType.NUMBER, 1],
    ['bundleFilesize', DynamicPropertyType.NUMBER, 150000],
];

export default class Creative extends DocumentFolder<
    CreativeSchema,
    CreativePublicSchema
> {
    schema = new CreativeSchema();
    publicSchema = new CreativePublicSchema();
    type = DocumentType.CREATIVE;
    clipboard = new ClipboardManager(this);
    collectionType = DocumentCollectionType.CREATIVE;

    public getDefaults() {
        const data = new CreativeSchema();
        applyDynamicFolderDefaults(data, this.parentFolder);
        return data;
    }

    generating = false;
    ads = new AdsManager(this);
    tearsheets = new Tearsheets(this);
    templates = new Templates(this);
    assets = new Assets(this);
    bundles = new BundlesManager(this);
    videoBundles = new VideoBundlesManager(this);
    dynamicProperties = new DynamicPropertiesManager(this, 'Creative');
    author = '';

    failed = false;
    error = '';
    displayPanel: PanelViews = PanelViews.NONE;
    viewScale = 1;
    currentPropertyManager: DynamicPropertiesManager | undefined =
        this.dynamicProperties;

    constructor(parent: DocumentFolderParent, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
    }

    onBeforeSave = async () => {
        this.data.filterSets = this.ads.filter.saved.getState().value;
    };

    setCurrentPropertyManager = (mode: PropertyManagerType) => {
        if (mode === PropertyManagerType.TEMPLATE) {
            this.currentPropertyManager =
                this.ads.current?.getTemplate()?.properties;
        } else if (mode === PropertyManagerType.AD) {
            this.currentPropertyManager = this.ads.current?.properties;
        } else if (mode === PropertyManagerType.CREATIVE) {
            this.currentPropertyManager = this.dynamicProperties;
        }
    };

    setDefaultDynamicProperties = (template?: Template) => {
        const props = this.dynamicProperties;

        for (const [name, type, value] of [
            ['template', DynamicPropertyType.TEMPLATE, template?.id ?? ''],
            ...DEFAULT_CONST_PROPERTIES,
        ]) {
            if (!props.getChild(name)) props.add(name, type, value);
        }

        if (template) {
            merge(props, template.properties, { addMissing: true });
        }

        props.resetChanged();
    };

    saveDynamicProperties = async () => {
        this.data.dynamicProperties = this.dynamicProperties.getData();
        this.dynamicProperties.resetChanged();
        await this.save();
    }

    onReady = () => {
        this.dynamicProperties.setState(this.data.dynamicProperties);
        this.dynamicProperties.resetChanged();
    }

    onLoad = async (_data: FolderSchema) => {
        await this.breadcrumbs.load();
        if (this.stub) return;
        await this.members.load();
        await this.assets.load();
        await this.templates.load();
        await this.tearsheets.load();
        await this.bundles.load();
        await this.videoBundles.load();
        await this.ads.load();
        this.ads.filter.saved.setState(this.data.filterSets);
    };

    onUpdated = () => {
        this.dynamicProperties.setState(this.data.dynamicProperties);
        this.dynamicProperties.resetChanged();
    }

    setPanel = (panelView: PanelViews) => {
        this.displayPanel = panelView;
    };

    setPanelOrToggle = (panelView: PanelViews) => {
        this.setPanel(
            panelView === this.displayPanel ? PanelViews.NONE : panelView
        );
    };

    setViewScale = (scale: number) => {
        this.viewScale = scale;
    }

    addSelectedFilterProperty = (property: IDynamicProperty<any>) => {
        this.ads.addSelectedFilterProperty(property);
    }

    removeSelectedFilterProperty = (property: IDynamicProperty<any>) => {
        this.ads.removeSelectedFilterProperty(property);
    }

    reset = () => {
        super.reset();
        this.status = DocumentStatus.NONE;

        this.generating = false;
        this.ads.reset();
        this.ads = new AdsManager(this);
        this.tearsheets.reset();
        this.tearsheets = new Tearsheets(this);
        this.templates.reset();
        this.templates = new Templates(this);
        this.assets.reset();
        this.assets = new Assets(this);
        this.bundles.reset();
        this.bundles = new BundlesManager(this);
        this.videoBundles.reset();
        this.videoBundles = new VideoBundlesManager(this);
        this.dynamicProperties.reset();
        this.dynamicProperties = new DynamicPropertiesManager(this);
        this.author = '';

        this.failed = false;
        this.error = '';
        this.displayPanel = PanelViews.NONE;
        this.viewScale = 1;
    }
}
